// src/features/subscription/DiscountFormDialog.tsx
// Create / edit a discount code — one form-kit dialog shared by both flows
// (docs/ux/14-forms.md), opened from DiscountListPage's "+ New" and from
// DiscountDetailPage's "Edit" button. Discount type uses a SegmentedControl
// (≤4 options) per the forms-wave mandate. When type is a fixed amount the
// value is entered/displayed in major units and converted to minor units on
// submit, same convention as GrantCreditModal; for percentage / free-period
// discounts the value is a plain number (percent or months).

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useSubscriptionDiscount, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ChipInput } from './ChipInput';
import { errMessage, toMajorUnitsText, toMinorUnits } from './format';
import type { Discount } from '@core/types/subscription/Discount';
import type { DiscountType } from '@core/types/subscription/DiscountType';
import type { Currency } from '@core/types/subscription/Currency';

const DISCOUNT_TYPES: DiscountType[] = ['DISCOUNT_TYPE_PERCENTAGE', 'DISCOUNT_TYPE_FIXED_AMOUNT', 'DISCOUNT_TYPE_FREE_PERIOD'];
const CURRENCIES: Currency[] = ['CURRENCY_USD', 'CURRENCY_EUR', 'CURRENCY_RUB', 'CURRENCY_GBP', 'CURRENCY_JPY', 'CURRENCY_CNY', 'CURRENCY_KZT', 'CURRENCY_BYN', 'CURRENCY_AED', 'CURRENCY_TRY'];

function digits(v: string): string {
  return v.replace(/[^0-9]/g, '');
}

function decimalText(v: string): string | null {
  return /^\d*\.?\d{0,2}$/.test(v) ? v : null;
}

function toInputDate(ts: string | undefined | null): string {
  if (!ts) return '';
  try { return new Date(ts).toISOString().slice(0, 10); } catch { return ''; }
}

interface FormState {
  code: string;
  name: string;
  description: string;
  type: DiscountType;
  value: string; // major units text when FIXED_AMOUNT, plain number otherwise
  currency: Currency;
  duration_months: string;
  max_redemptions: string;
  first_time_only: boolean;
  is_active: boolean;
  starts_at: string;
  ends_at: string;
  applicable_plans: string[];
}

function snapshotOf(discount: Discount | null): FormState {
  const type = discount?.type ?? 'DISCOUNT_TYPE_PERCENTAGE';
  return {
    code: discount?.code ?? '',
    name: discount?.name ?? '',
    description: discount?.description ?? '',
    type,
    value: type === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? toMajorUnitsText(discount?.value) : (discount?.value ? String(discount.value) : ''),
    currency: discount?.currency ?? 'CURRENCY_USD',
    duration_months: discount?.duration_months ? String(discount.duration_months) : '',
    max_redemptions: discount?.max_redemptions ? String(discount.max_redemptions) : '',
    first_time_only: discount?.first_time_only ?? false,
    is_active: discount?.is_active ?? true,
    starts_at: toInputDate(discount?.starts_at),
    ends_at: toInputDate(discount?.ends_at),
    applicable_plans: discount?.applicable_plans ?? [],
  };
}

export interface DiscountFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Discount being edited; null → create. */
  discount: Discount | null;
  onSaved: () => void;
}

export const DiscountFormDialog = observer(({ open, onClose, discount, onSaved }: DiscountFormDialogProps) => {
  const { t } = useI18n();
  const store = useSubscriptionDiscount();
  const isEdit = !!discount;

  const [form, setForm] = useState<FormState>(() => snapshotOf(discount));
  const [initial, setInitial] = useState<FormState>(() => snapshotOf(discount));
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const snap = snapshotOf(discount);
    setForm(snap);
    setInitial(snap);
    setMoreFields(false);
    setError(null);
  }, [open, discount]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const canSubmit = form.code.trim().length > 0 && form.name.trim().length > 0;

  const typeOptions = DISCOUNT_TYPES.map((v) => ({ value: v, label: t(`subscription.discount.type.${v.replace('DISCOUNT_TYPE_', '').toLowerCase()}`) }));
  const currencyOptions = CURRENCIES.map((v) => ({ value: v, label: t(`subscription.currency.${v.replace('CURRENCY_', '').toLowerCase()}`) }));

  const resolvedValue = () =>
    form.type === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? toMinorUnits(form.value) : Number(form.value || 0);

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && discount) {
        await store.UpdateDiscount(discount.entity?.id ?? '', {
          discount: {
            ...discount,
            code: form.code.trim(),
            name: form.name.trim(),
            description: form.description,
            type: form.type,
            value: resolvedValue(),
            currency: form.type === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? form.currency : discount.currency,
            duration_months: form.duration_months ? Number(form.duration_months) : 0,
            max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : 0,
            first_time_only: form.first_time_only,
            is_active: form.is_active,
            starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : undefined,
            ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
            applicable_plans: form.applicable_plans,
          },
          field_mask: ['code', 'name', 'description', 'type', 'value', 'currency', 'duration_months', 'max_redemptions', 'first_time_only', 'is_active', 'starts_at', 'ends_at', 'applicable_plans'],
        });
      } else {
        await store.CreateDiscount({
          code: form.code.trim(),
          name: form.name.trim(),
          description: form.description,
          type: form.type,
          value: resolvedValue(),
          currency: form.type === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? form.currency : undefined,
          duration_months: form.duration_months ? Number(form.duration_months) : undefined,
          max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : undefined,
          first_time_only: form.first_time_only,
          starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : undefined,
          ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : undefined,
          applicable_plans: form.applicable_plans,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('subscription.discount.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('subscription.discount.create')}
      canSubmit={canSubmit}
      isSubmitting={saving}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('subscription.discount.edit') : t('subscription.discount.create')}
      footerLeft={
        <button type="button" onClick={() => setMoreFields((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground">
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <Alert variant="error">{error}</Alert>}
        <TitleInput
          value={form.name}
          onChange={(v) => set('name', v)}
          placeholder={`${t('subscription.discount.field.name')}…`}
          initialFocus
          aria-label={t('subscription.discount.field.name')}
        />
        <AutoGrowTextarea
          value={form.description}
          onChange={(v) => set('description', v)}
          placeholder={`${t('subscription.discount.field.description')}…`}
          minRows={1}
        />
      </div>

      <div className="px-3.5 pb-2">
        <FormRow label={t('subscription.discount.field.code')} labelWidth="sm">
          <input
            value={form.code}
            onChange={(e) => set('code', e.target.value)}
            placeholder={t('subscription.discount.field.codePlaceholder')}
            aria-label={t('subscription.discount.field.code')}
            className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <SegmentedControl size="sm" value={form.type} onChange={(v) => set('type', v as DiscountType)} options={typeOptions} aria-label={t('subscription.discount.field.type')} />
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <input
            type="text"
            inputMode="decimal"
            value={form.value}
            onChange={(e) => { const v = decimalText(e.target.value); if (v !== null) set('value', v); }}
            placeholder="0"
            aria-label={t('subscription.discount.field.value')}
            className="w-16 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
          <span className="text-xs text-muted-foreground">
            {form.type === 'DISCOUNT_TYPE_PERCENTAGE' ? '%' : form.type === 'DISCOUNT_TYPE_FIXED_AMOUNT' ? form.currency.replace('CURRENCY_', '') : t('subscription.discount.field.months')}
          </span>
        </span>
        {form.type === 'DISCOUNT_TYPE_FIXED_AMOUNT' && (
          <div className="w-28">
            <Select value={form.currency} onChange={(e) => set('currency', e.target.value as Currency)} options={currencyOptions} aria-label={t('subscription.discount.field.currency')} fullWidth />
          </div>
        )}
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <input type="checkbox" checked={form.first_time_only} onChange={(e) => set('first_time_only', e.target.checked)} className="h-4 w-4 rounded border-input" />
          {t('subscription.discount.field.firstTimeOnly')}
        </label>
      </div>

      {moreFields && (
        <div className="space-y-3 border-t border-border px-3.5 py-3">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
              <span className="text-xs text-muted-foreground">{t('subscription.discount.field.durationMonths')}</span>
              <input
                type="text"
                inputMode="numeric"
                value={form.duration_months}
                onChange={(e) => set('duration_months', digits(e.target.value))}
                placeholder="0"
                aria-label={t('subscription.discount.field.durationMonths')}
                className="w-12 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
              />
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
              <span className="text-xs text-muted-foreground">{t('subscription.discount.field.maxRedemptions')}</span>
              <input
                type="text"
                inputMode="numeric"
                value={form.max_redemptions}
                onChange={(e) => set('max_redemptions', digits(e.target.value))}
                placeholder={t('subscription.discount.field.unlimited')}
                aria-label={t('subscription.discount.field.maxRedemptions')}
                className="w-16 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
              />
            </span>
          </div>

          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
            <FormRow label={t('subscription.discount.field.startsAt')} labelWidth="sm">
              <input type="date" value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('subscription.discount.field.endsAt')} labelWidth="sm">
              <input type="date" value={form.ends_at} onChange={(e) => set('ends_at', e.target.value)} className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
          </div>

          <FormRow label={t('subscription.discount.field.applicablePlans')} labelWidth="sm" hint={t('subscription.discount.field.applicablePlansHint')}>
            <ChipInput value={form.applicable_plans} onChange={(v) => set('applicable_plans', v)} placeholder={t('subscription.discount.field.applicablePlansPlaceholder')} aria-label={t('subscription.discount.field.applicablePlans')} />
          </FormRow>

          {isEdit && (
            <ToggleSwitch label={t('subscription.discount.field.isActive')} checked={form.is_active} onChange={(v) => set('is_active', v)} />
          )}
        </div>
      )}
    </FormDialog>
  );
});

export default DiscountFormDialog;
